import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AppNotificationService } from "src/app/core/services/app-notification.service";
import { AccountService } from "src/app/modules/authentication/services/account.service";
import { AppService } from "src/services/app.service";
import {
  ErrorCodes,
  ErrorLevel,
  ErrorMessages,
  ErrorResponse,
} from "../models/error";
import { PAGE_URLS } from "../models/general";
import { checkForErrors } from "../utils/error-handler";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private appNotificationService: AppNotificationService,
    private accountService: AccountService,
    private appService: AppService,
    private router: Router,
  ) {}
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          checkForErrors(event.body, this.appNotificationService);
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.Unauthorized) {
          if (this.accountService.isLoggedIn()) {
            console.warn(
              "ErrorInterceptor: Caught 401 for logged-in user. Assuming TokenInterceptor handled refresh/logout.",
            );
          } else {
            this.router.navigate([PAGE_URLS.LOGIN]);
            console.warn(
              "ErrorInterceptor: Caught 401 for non-logged-in user. Redirecting to login.",
            );
          }
        } else if (this.isAuthRequest(request)) {
          const errorResponse = this.handleErrorResponse(error);
          this.appNotificationService.showError(errorResponse.message);
        } else if (error.status === HttpStatusCode.ImATeapot) {
          const { message, level } = error.error;
          if (level === ErrorLevel.INFO) {
            this.appNotificationService.showSuccess(message);
          } else if (level === ErrorLevel.WARNING) {
            this.appNotificationService.showError(message);
          }
        } else if (error.status === HttpStatusCode.Forbidden) {
          this.appNotificationService.showError(
            ErrorMessages.NOT_HAVE_PERMISSION,
          );
        }
        return throwError(() => this.handleErrorResponse(error));
      }),
    );
  }

  isAuthRequest(request: HttpRequest<any>): boolean {
    const authRequests = ["auth/flows", "/auth/reset-password", "/auth/login"];
    if (request.method === "POST") {
      authRequests.push("/admins/");
    }
    return authRequests.some((url) => request.url.includes(url));
  }

  /**
   * Handle error response and return ErrorResponse
   */
  private handleErrorResponse(error: HttpErrorResponse): ErrorResponse {
    let message = error.message ? error.message : error.statusText;
    let status = error.status;
    let code = error.status;
    let meta = null;
    const responseError = error.error;
    if (responseError) {
      if (error && responseError) {
        message = responseError.message;
        meta = responseError.meta ? responseError.meta : null;
        code = responseError.code ? parseInt(responseError.code, 0) : 0;

        if (responseError.errors) {
          message = "";
          // try to get the message from new format error
          Object.values(responseError.errors).forEach((value, index) => {
            const separator = index > 0 ? "\n" : "";
            message += `${separator}${value}`;
          });
          // if there is no message in new format, try to get the message from old format error
          if (Array.isArray(responseError.errors)) {
            responseError.errors.forEach((errorItem: any) => {
              if (errorItem.code) {
                code = parseInt(errorItem.code, 0);
              }
              if (errorItem.detail) {
                message = errorItem.detail;
              }
              if (errorItem.meta) {
                meta = errorItem.meta;
              }
              if (errorItem.status) {
                status = parseInt(errorItem.status, 0);
              }
            });
          }
        } else if (responseError.code === ErrorCodes.PASSWORD_EXPIRED) {
          this.appService.setIsRequiredToChangePassword(true);
          this.router.navigate([PAGE_URLS.EDIT_PASSWORD]);
        }
      } else {
        message = ErrorMessages.AN_ERROR_HAS_OCCURRED;
      }
    }
    const errorResponse: ErrorResponse = {
      status,
      code,
      message,
      meta,
      originalError: responseError.errors,
    };

    return errorResponse;
  }
}
